import { textureLoaderService } from "../texture/TextureLoaderService";
import { GridHelper, Mesh, MeshBasicMaterial, BackSide, SphereGeometry, Vector3, Color } from "three";
import { mainScene } from "../mainScene";
import { ROOT_SCALE } from "../rootScale";
import { RenderOrder } from "../RenderOrder";
import { localPlayerContainer } from "../../localplayer/LocalPlayerContainer";
import { RoomChangedMessage, RoomEnvironmentFacet, ChangeBackgroundMessage } from "flavr-common";
import { INFINITE_PLANE_MATERIAL } from "../shader/infinite.plane.material";
import { Module } from "../../module";
import { assertIsWeb } from "../../../messages/environment";
import { renderer } from "../RendererGlobals";
import { environmentSceneService } from "./EnvironmentSceneService";
import { GenerateSkyboxMessage, ResponseGenerateSkyboxMessage, SetGeneratedSkyboxMessage } from "../../../messages/rendering/SkyboxMessages";
import { modelPool } from "@modules/model";
assertIsWeb("BackgroundModule");
export function cloneUniforms(src) {
    const dst = {};
    for (const u in src) {
        dst[u] = {};
        for (const p in src[u]) {
            const property = src[u][p];
            if (property &&
                (property.isColor ||
                    property.isMatrix3 ||
                    property.isMatrix4 ||
                    property.isVector2 ||
                    property.isVector3 ||
                    property.isVector4 ||
                    property.isTexture ||
                    property.isQuaternion)) {
                dst[u][p] = property.clone();
            }
            else if (Array.isArray(property)) {
                dst[u][p] = property.slice();
            }
            else {
                dst[u][p] = property;
            }
        }
    }
    return dst;
}
const backgrounds = [
    {
        path: "img/WildSky/",
        files: [
            "WildSky_A_13_right.png",
            "WildSky_A_13_left.png",
            "WildSky_A_13_up.png",
            "WildSky_A_13_down.png",
            "WildSky_A_13_forward.png",
            "WildSky_A_13_back.png"
        ]
    },
    {
        path: "img/hills/",
        files: ["Hills_A_13_right.png", "Hills_A_13_left.png", "Hills_A_13_up.png", "Hills_A_13_down.png", "Hills_A_13_forward.png", "Hills_A_13_back.png"]
    },
    {
        path: "img/hills/",
        files: ["Hills_A_13_right.png", "Hills_A_13_left.png", "Hills_A_13_up.png", "gray.png", "Hills_A_13_forward.png", "Hills_A_13_back.png"]
    },
    {
        path: "img/plain_landscape/",
        files: ["right.jpg", "left.jpg", "up.jpg", "down.jpg", "front.jpg", "back.jpg"]
    },
    {
        path: "img/landscape4/",
        files: ["right.jpg", "left.jpg", "up.jpg", "down.jpg", "front.jpg", "back.jpg"]
    }
];
const DEFAULT_BACKGROUND = 2;
const ENABLE_COORDINATE_SYSTEM = window.location.href.includes("coords");
const DEFAULT_ENVIRONMENT_PARAMS = {
    showSkybox: true,
    skyboxIndex: DEFAULT_BACKGROUND,
    useSkyboxAsEnvironmentMap: true,
    backgroundColor: 0x000000,
    showInfinitePlane: false,
    infinitePlaneColor: 0xa0a0a0,
    infinitePlaneElevation: 0.0
};
/**
 * Controls the room background, skybox and environment presentation so visual ambience can be changed independently from the rest of the scene graph.
 *
 * Facets:
 * - `RoomEnvironmentFacet` - Stores a room's environment parameters (skybox, background color, infinite plane settings); if the facet belongs to the room the client is currently in, the parameters are applied immediately.
 *
 * Messages:
 * - `ChangeBackgroundMessage` - Applies a new background configuration immediately.
 * - `GenerateSkyboxMessage` - Generates a cube map from a world position or model target; answers with a generated-map id when asked, or otherwise applies/stores it directly for the current or a specified room.
 * - `SetGeneratedSkyboxMessage` - Applies a previously generated skybox by its id.
 * - `RoomChangedMessage` - Reapplies the stored environment parameters, and any pending generated skybox, for the newly active room.
 */
export class BackgroundModule extends Module {
    /**
     * Sets up the default clear color and registers the runtime subscriptions (room environment facets, skybox
     * generation/application, background changes and room switches) that keep the scene background in sync.
     *
     * @param loadDefaultSkyboxAndModels - if false, `init` skips loading the default skybox/environment maps.
     */
    constructor(loadDefaultSkyboxAndModels = true) {
        var _a;
        super("BackgroundModule");
        this.loadDefaultSkyboxAndModels = loadDefaultSkyboxAndModels;
        this.skyMaterialArray = [];
        this.generatedEnviromentMaps = [];
        this.roomEnvironmentParameters = new Map();
        this.roomInfos = new Map();
        this.handleChangeBackgroundMessage = (msg) => {
            this.update(msg.params, false);
        };
        this.handleGenerateSkyboxMessage = (msg, askId) => {
            const params = {
                near: 0.1,
                far: 1000,
                resolution: 512
            };
            const worldPosition = new Vector3();
            if (typeof msg.target === "number") {
                const model = modelPool.getModel(msg.target);
                if (model !== undefined) {
                    worldPosition.copy(model.getWorldPosition());
                    params.near = model.getWorldScale().length() + 0.1;
                }
                params.affectedUids = [msg.target];
            }
            else {
                worldPosition.fromArray(msg.target);
            }
            const envMap = environmentSceneService.generateCubeMap(worldPosition, params);
            if (askId !== undefined) {
                const id = this.generatedEnviromentMaps.length;
                this.generatedEnviromentMaps.push(envMap);
                this.connection.answer(askId, new ResponseGenerateSkyboxMessage(id));
                return;
            }
            if (msg.targetRoomUid !== undefined) {
                let roomInfo = this.roomInfos.get(msg.targetRoomUid);
                if (roomInfo === undefined) {
                    roomInfo = {};
                    this.roomInfos.set(msg.targetRoomUid, roomInfo);
                }
                roomInfo.skyMap = envMap;
            }
            if (msg.targetRoomUid === undefined || msg.targetRoomUid === this.currentRoomUid) {
                this.setGeneratedSkybox(envMap);
            }
        };
        this.handleSetGeneratedSkyboxMessage = (msg) => {
            const texture = this.generatedEnviromentMaps[msg.id];
            if (texture === undefined)
                return;
            this.setGeneratedSkybox(texture);
        };
        this.handleRoomChangedMessage = (msg) => {
            var _a;
            this.update((_a = this.roomEnvironmentParameters.get(msg.uid)) !== null && _a !== void 0 ? _a : {}, true);
            this.currentRoomUid = msg.uid;
            const roomInfo = this.roomInfos.get(msg.uid);
            if ((roomInfo === null || roomInfo === void 0 ? void 0 : roomInfo.skyMap) !== undefined) {
                this.setGeneratedSkybox(roomInfo.skyMap);
            }
        };
        this.connection.subscribeByFacetType(RoomEnvironmentFacet, (uid, facet) => {
            const model = modelPool.getModel(uid);
            if (model === undefined) {
                console.warn("RoomEnvironmentFacet with invalid uid", uid);
                return;
            }
            const roomModel = model.isRoom() ? model : model.getRoom();
            if (roomModel === undefined) {
                console.warn("RoomEnvironmentFacet with invalid roomId", model);
                return;
            }
            this.roomEnvironmentParameters.set(roomModel.getUID(), facet.params);
            if (this.currentRoomUid === roomModel.getUID()) {
                this.update(facet.params, true);
            }
        });
        this.connection.subscribeByTypeOrAskRequest(GenerateSkyboxMessage, this.handleGenerateSkyboxMessage);
        this.connection.subscribeByType(SetGeneratedSkyboxMessage, this.handleSetGeneratedSkyboxMessage);
        this.connection.subscribeByType(RoomChangedMessage, this.handleRoomChangedMessage);
        this.connection.subscribeByType(ChangeBackgroundMessage, this.handleChangeBackgroundMessage);
        renderer.setClearColor(new Color().setHex((_a = DEFAULT_ENVIRONMENT_PARAMS.backgroundColor) !== null && _a !== void 0 ? _a : 0x000000));
    }
    /**
     * Completes the background setup and registers the runtime callbacks that keep the active room background synchronized with incoming messages.
     */
    init() {
        this.createSkyboxMaterial();
        if (this.loadDefaultSkyboxAndModels) {
            this.defaultBackgroundMap = this.loadBackground(backgrounds[DEFAULT_BACKGROUND]);
            this.defaultEnvironmentMap = this.defaultBackgroundMap.clone();
        }
        if (ENABLE_COORDINATE_SYSTEM) {
            this.createCoordinateSystem();
        }
        this.createInfinitePlane();
    }
    loadBackground(background) {
        textureLoaderService.setPath(background.path);
        this.backgroundMap = textureLoaderService.loadCube(background.files);
        this.environmentMap = textureLoaderService.loadCube(background.files);
        mainScene.background = this.backgroundMap;
        mainScene.environment = this.environmentMap;
        environmentSceneService.setDefaultBackground(this.backgroundMap);
        environmentSceneService.setDefaultEnvironment(this.environmentMap);
        for (let i = 0; i < background.files.length; ++i) {
            const url = background.files[i];
            // Avoid rendering a transparent background while the skybox texture is loading
            textureLoaderService.loadTexture(url, (texture) => {
                const mat = this.skyMaterialArray[i];
                mat.map = texture;
                mat.needsUpdate = true;
            });
        }
        textureLoaderService.setPath("");
        return this.environmentMap;
    }
    update(params, useDefaults) {
        var _a, _b, _c;
        const materialsNeedUpdate = false;
        if (this.defaultBackgroundMap !== undefined)
            mainScene.background = this.defaultBackgroundMap;
        if (this.defaultEnvironmentMap !== undefined)
            mainScene.environment = this.defaultEnvironmentMap;
        if (params.skyboxIndex !== undefined && params.skyboxIndex < backgrounds.length) {
            this.loadBackground(backgrounds[params.skyboxIndex]);
        }
        if (useDefaults) {
            params = Object.assign(Object.assign({}, DEFAULT_ENVIRONMENT_PARAMS), params);
        }
        renderer.setClearColor((_a = params.backgroundColor) !== null && _a !== void 0 ? _a : 0);
        if (params.skyboxIndex !== undefined && params.skyboxIndex < backgrounds.length) {
            this.loadBackground(backgrounds[params.skyboxIndex]);
        }
        if (params.showInfinitePlane !== undefined && this.infinitePlaneMesh !== undefined) {
            this.infinitePlaneMesh.visible = params.showInfinitePlane;
        }
        if (params.infinitePlaneColor !== undefined) {
            INFINITE_PLANE_MATERIAL.uniforms.uColor.value.setHex(params.infinitePlaneColor);
        }
        if (params.infinitePlaneElevation !== undefined) {
            INFINITE_PLANE_MATERIAL.uniforms.uPlainElevation.value = params.infinitePlaneElevation;
        }
        INFINITE_PLANE_MATERIAL.uniforms.uUseTexture.value = 0;
        if (materialsNeedUpdate) {
            mainScene.traverse((node) => {
                if (node instanceof Mesh) {
                    const mat = node.material;
                    if (Array.isArray(mat)) {
                        for (const m of mat) {
                            m.needsUpdate = true;
                        }
                    }
                    else if (mat) {
                        node.material.needsUpdate = true;
                    }
                }
            });
        }
        if (params.showSkybox !== undefined)
            mainScene.background = params.showSkybox ? ((_b = this.backgroundMap) !== null && _b !== void 0 ? _b : null) : null;
        if (params.useSkyboxAsEnvironmentMap !== undefined)
            mainScene.environment = params.useSkyboxAsEnvironmentMap ? ((_c = this.environmentMap) !== null && _c !== void 0 ? _c : null) : null;
        for (const it of this.skyMaterialArray) {
            it.color.setHSL(0, 0, 1);
        }
        const lightness = params.showSkybox ? 1 : 0;
        INFINITE_PLANE_MATERIAL.uniforms.uFogColor.value.setHSL(0, 0, lightness);
    }
    createSkyboxMaterial() {
        for (let i = 0; i < 6; i++) {
            const mat = new MeshBasicMaterial({
                side: BackSide,
                transparent: false
            });
            mat.depthTest = false;
            mat.depthWrite = false;
            this.skyMaterialArray.push(mat);
        }
    }
    createInfinitePlane() {
        const geometry = new SphereGeometry(1, 4, 1, undefined, undefined, 0.5 * Math.PI, 0.5 * Math.PI);
        this.infinitePlaneMesh = new Mesh(geometry, INFINITE_PLANE_MATERIAL);
        this.infinitePlaneMesh.renderOrder = RenderOrder.INFINITE_PLANE;
        this.infinitePlaneMesh.frustumCulled = false;
        this.infinitePlaneMesh.matrixAutoUpdate = false;
        this.infinitePlaneMesh.receiveShadow = true;
        this.infinitePlaneMesh.castShadow = false;
        this.infinitePlaneMesh.visible = false;
        localPlayerContainer.localPlayerRootNode.add(this.infinitePlaneMesh);
    }
    createCoordinateSystem() {
        const scale = 10 * ROOT_SCALE;
        const divisions = 10;
        const gridXZ = new GridHelper(scale, divisions, 0xff0000, 0x0000ff);
        const gridXY = new GridHelper(scale, divisions, 0xff0000, 0xffff00);
        const gridYZ = new GridHelper(scale, divisions, 0xff0000, 0x00ffff);
        gridXY.rotateX(Math.PI / 2);
        gridYZ.rotateZ(Math.PI / 2);
        mainScene.add(gridXY);
        mainScene.add(gridXZ);
        mainScene.add(gridYZ);
    }
    setGeneratedSkybox(texture) {
        mainScene.background = texture;
        mainScene.environment = texture;
    }
}
